import apiClient from '@/lib/api-client';
import type { DashboardStats } from '@/types/dashboard';
import type { Booking, AvailableSlot } from '@/types/booking';

interface RevenueData {
  period: string;
  revenue: number;
  bookings: number;
}

interface BulkSlotData {
  providerId: number;
  serviceId: number;
  dates: string[];
  startTime: string;
  endTime: string;
}

interface RecurringSlotData {
  providerId: number;
  serviceId: number;
  startDate: string;
  endDate: string;
  weekDays: number[];
  startTime: string;
  endTime: string;
}

interface TimeRangeSlotData {
  providerId: number;
  serviceId: number;
  date: string;
  startTime: string;
  endTime: string;
  maxCapacity?: number;
}

class AdminService {
  // Dashboard
  async getDashboard(): Promise<DashboardStats> {
    const { data } = await apiClient.get<{ data: DashboardStats }>('/v1/admin/dashboard');
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async getDashboardStats(period?: 'day' | 'week' | 'month' | 'year'): Promise<DashboardStats> {
    const { data } = await apiClient.get<{ data: any }>('/v1/admin/dashboard/stats', {
      params: { period }
    });

    // Transform snake_case to camelCase
    const rawStats = data.data;
    return {
      totalBookings: rawStats.total_bookings || 0,
      pendingBookings: rawStats.pending_bookings || 0,
      confirmedBookings: rawStats.confirmed_bookings || 0,
      todayBookings: rawStats.today_bookings || 0,
      weeklyRevenue: parseFloat(rawStats.weekly_revenue || '0'),
      monthlyRevenue: parseFloat(rawStats.monthly_revenue || '0'),
      topServices: rawStats.top_services?.map((service: any) => ({
        id: service.id,
        name: service.name,
        count: service.bookings_count || service.count || 0
      })) || [],
      topProviders: rawStats.top_providers?.map((provider: any) => ({
        id: provider.id,
        name: provider.name,
        count: provider.bookings_count || provider.count || 0
      })) || []
    };
  }

  async getRecentBookings(limit: number = 10): Promise<Booking[]> {
    const { data } = await apiClient.get<{ data: any[] }>('/v1/admin/dashboard/recent_bookings', {
      params: { limit }
    });
    // Transform snake_case to camelCase
    return data.data.map(booking => ({
      id: booking.id,
      publicId: booking.public_id,
      bookingCode: booking.booking_code,
      status: booking.status,
      customerFirstName: booking.customer_first_name,
      customerLastName: booking.customer_last_name,
      customerEmail: booking.customer_email,
      customerPhone: booking.customer_phone,
      notes: booking.notes,
      availableSlotId: booking.available_slot_id,
      organizationId: booking.organization_id,
      createdAt: booking.created_at,
      updatedAt: booking.updated_at,
      confirmedAt: booking.confirmed_at,
      cancelledAt: booking.cancelled_at,
      availableSlot: booking.available_slot ? {
        id: booking.available_slot.id,
        startTime: booking.available_slot.start_time,
        endTime: booking.available_slot.end_time,
        available: booking.available_slot.is_available,
        service: booking.available_slot.service ? {
          id: booking.available_slot.service.id,
          name: booking.available_slot.service.name,
          duration_minutes: booking.available_slot.service.duration_minutes,
          price: booking.available_slot.service.price
        } : undefined,
        provider: booking.available_slot.provider ? {
          id: booking.available_slot.provider.id,
          name: booking.available_slot.provider.name,
          displayName: booking.available_slot.provider.name
        } : undefined
      } : undefined
    } as unknown as Booking));
  }

  async getRevenue(period: 'week' | 'month' | 'year'): Promise<RevenueData[]> {
    const { data } = await apiClient.get<{ data: RevenueData[] }>('/v1/admin/dashboard/revenue', {
      params: { period }
    });
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  // Admin Booking Operations
  async completeBooking(id: number): Promise<Booking> {
    const { data } = await apiClient.post<{ data: Booking }>(`/v1/admin/bookings/${id}/complete`);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async markNoShow(id: number): Promise<Booking> {
    const { data } = await apiClient.post<{ data: Booking }>(`/v1/admin/bookings/${id}/no_show`);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  // Admin Slot Management
  async createBulkSlots(slots: BulkSlotData): Promise<AvailableSlot[]> {
    const { data } = await apiClient.post<{ data: AvailableSlot[] }>('/v1/admin/available_slots/bulk_create', slots);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async generateRecurringSlots(config: RecurringSlotData): Promise<AvailableSlot[]> {
    const { data } = await apiClient.post<{ data: AvailableSlot[] }>('/v1/admin/available_slots/generate_recurring', config);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async generateSlotsFromTimeRange(config: TimeRangeSlotData): Promise<{ slots: AvailableSlot[]; count: number }> {
    const payload = {
      provider_id: config.providerId,
      service_id: config.serviceId,
      date: config.date,
      start_time: config.startTime,
      end_time: config.endTime,
      max_capacity: config.maxCapacity || 1
    };

    const { data } = await apiClient.post<{ data: { slots: AvailableSlot[]; count: number } }>(
      '/v1/admin/available_slots/generate_from_time_range',
      payload
    );
    return data.data; // Unwrap: backend returns {success: true, data: {slots: [...], count: N}}
  }

  async toggleSlotAvailability(id: number): Promise<AvailableSlot> {
    const { data } = await apiClient.post<{ data: AvailableSlot }>(`/v1/admin/available_slots/${id}/toggle_available`);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async createSlot(slot: Omit<AvailableSlot, 'id' | 'createdAt' | 'updatedAt'>): Promise<AvailableSlot> {
    const { data } = await apiClient.post<{ data: AvailableSlot }>('/v1/admin/available_slots', slot);
    return data.data; // Unwrap: backend returns {data: {...}, meta: {...}}
  }

  async updateSlot(id: number, updates: Partial<AvailableSlot> | any): Promise<AvailableSlot> {
    // The API expects snake_case fields, so we pass updates as-is if already formatted
    // The slots.tsx component already formats the data correctly with snake_case
    const response = await apiClient.put(`/v1/admin/available_slots/${id}`, {
      available_slot: updates
    });
    return response.data.data?.slot || response.data;
  }

  async deleteSlot(id: number): Promise<void> {
    await apiClient.delete(`/v1/admin/available_slots/${id}`);
  }
}

export const adminService = new AdminService();